import React, { useState, useEffect } from 'react';
import { getAllTechnologists, getOrCreateConversation, getConversationsForUser } from '../../../services/chatService';
import Icon from '../../../components/AppIcon';

export default function TechnicianSelector({ doctorId, doctorName, onSelectConversation, activeConvId }) {
  const [technologists, setTechnologists] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [tab, setTab] = useState('conversations');

  useEffect(() => {
    Promise.all([
      getAllTechnologists(),
      getConversationsForUser(doctorId, 'doctor'),
    ])?.then(([techs, convs]) => {
        setTechnologists(techs);
        setConversations(convs);
      })?.catch(console.error)?.finally(() => setLoading(false));
  }, [doctorId]);

  const startChat = async (tech) => {
    setStarting(tech?.id);
    try {
      const conv = await getOrCreateConversation(doctorId, tech?.id, doctorName, tech?.full_name);
      setConversations((prev) => {
        if (prev?.find((c) => c?.id === conv?.id)) return prev;
        return [conv, ...prev];
      });
      onSelectConversation(conv);
      setTab('conversations');
    } catch (err) {
      console.error('Error starting chat:', err);
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex gap-1 p-2 border-b border-border bg-card">
        {[
          { key: 'conversations', label: 'Chats', icon: 'MessageSquare' },
          { key: 'technologists', label: 'Technologists', icon: 'Users' },
        ]?.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-colors ${
              tab === key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={icon} size={14} />
            {label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : tab === 'conversations' ? (
          conversations?.length === 0 ? (
            <div className="text-center py-8 space-y-2 px-4">
              <Icon name="MessageCircle" size={28} className="text-muted-foreground mx-auto" />
              <p className="text-xs text-muted-foreground">No active chats. Go to "Technologists" to start one.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {conversations?.map((conv) => (
                <button
                  key={conv?.id}
                  onClick={() => onSelectConversation(conv)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    activeConvId === conv?.id ? 'bg-primary/10' : 'hover:bg-muted'
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={15} className="text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{conv?.tech_name || 'Technologist'}</p>
                    <p className="text-xs text-muted-foreground truncate">{conv?.last_message || 'No messages yet'}</p>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          technologists?.length === 0 ? (
            <div className="text-center py-8 space-y-2 px-4">
              <Icon name="Users" size={28} className="text-muted-foreground mx-auto" />
              <p className="text-xs text-muted-foreground">No technologists registered yet.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {technologists?.map((tech) => (
                <div key={tech?.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={15} className="text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{tech?.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{tech?.specialization} · {tech?.facility}</p>
                  </div>
                  <button
                    onClick={() => startChat(tech)}
                    disabled={starting === tech?.id}
                    className="flex-shrink-0 px-2.5 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
                  >
                    {starting === tech?.id
                      ? <div className="w-3 h-3 border border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      : 'Chat'
                    }
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
